import TextField, { OutlinedTextFieldProps } from '@mui/material/TextField';
import { FC, useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { getCurrencySymbol } from '../../utils';

type Props = Pick<
  OutlinedTextFieldProps,
  | 'name'
  | 'id'
  | 'fullWidth'
  | 'size'
  | 'placeholder'
  | 'disabled'
  | 'error'
  | 'helperText'
  | 'slotProps'
  | 'onFocus'
> & {
  value?: number | null;
  min?: number;
  max?: number;
  decimals?: number;
  currency?: string;
  onChange?: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
  hideZeroValue?: boolean;
  showPercentSuffix?: boolean;
  clearZeroValueOnFocus?: boolean;
};

const NumberInput: FC<Props> = ({
  decimals = 0,
  size = 'small',
  value,
  onChange,
  onBlur,
  currency,
  hideZeroValue = false,
  showPercentSuffix = false,
  clearZeroValueOnFocus = false,
  ...rest
}) => {
  const [displayValue, setDisplayValue] = useState<Props['value'] | ''>(value);
  const hasChangedRef = useRef(false);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const getValue = (value: string) => {
    const rawValue = currency
      ? value.replace(getCurrencySymbol(currency), '')
      : value;

    return Number(
      rawValue.replace(showPercentSuffix ? '%' : '', '').replaceAll(/,/g, ''),
    );
  };

  return (
    <NumericFormat
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      value={hideZeroValue ? displayValue || '' : displayValue}
      customInput={TextField}
      prefix={currency ? getCurrencySymbol(currency) : undefined}
      type="text"
      decimalScale={decimals}
      allowNegative={false}
      allowLeadingZeros={false}
      suffix={showPercentSuffix ? '%' : undefined}
      onFocus={() => {
        if (clearZeroValueOnFocus && !value) {
          hasChangedRef.current = false;
          setDisplayValue('');
        }
      }}
      onChange={(event) => {
        hasChangedRef.current = true;
        onChange?.(getValue(event.target.value));
      }}
      onBlur={(event) => {
        if (clearZeroValueOnFocus && !hasChangedRef.current) {
          setDisplayValue(value);
        } else {
          onBlur?.(getValue(event.target.value));
        }

        hasChangedRef.current = false;
      }}
      thousandSeparator
      size={size}
      variant="outlined"
      {...rest}
    />
  );
};

export default NumberInput;
